import { Select as AntdSelect, Modal, notification } from "antd";
import {
  Autocomplete,
  Button,
  Input,
  Select,
  Table,
} from "@mantine/core";
import {
  IconBuildingWarehouse,
  IconCircleCheck,
  IconFileText,
  IconHash,
  IconHome,
  IconMapPin,
  IconPhoto,
  IconStack2,
  IconToggleLeft,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const defaultTempArea = {
  name: "",
  enable: true,
  description: "",
  picture: "",
  storageLocations: [],
};

const defaultTempStorageLocation = {
  name: "",
  enable: true,
  description: "",
  picture: "",
};

function AddWarehouseListPage() {
  const { t } = useTranslation();
  const [employeeData, setEmployeeData] = useState([]);
  const [warehouseData, setWarehouseData] = useState({
    name: "",
    enable: true,
    description: "",
    address: {
      city: "",
      district: "",
      postalCode: "",
      address: "",
    },
  });
  const [areas, setAreas] = useState([]);

  const [areaModalOpen, setAreaModalOpen] = useState(false);
  const [areaModalMode, setAreaModalMode] = useState("new");
  const [editAreaIndex, setEditAreaIndex] = useState(0);
  const [tempArea, setTempArea] = useState(defaultTempArea);

  const [locModalOpen, setLocModalOpen] = useState(false);
  const [locModalMode, setLocModalMode] = useState("new");
  const [locAreaIndex, setLocAreaIndex] = useState(0);
  const [editLocIndex, setEditLocIndex] = useState(0);
  const [tempLoc, setTempLoc] = useState(defaultTempStorageLocation);

  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();

  const handleSubmit = async (warehouseData, areas) => {
    try {
      const submitData = {
        ...warehouseData,
        address: {
          ...warehouseData.address,
          postalCode:
            warehouseData.address.postalCode === ""
              ? 0
              : Number(warehouseData.address.postalCode),
        },
      };

      const result = await single(orpcClient.warehouse.create([submitData]));
      if (result) {
        for (const area of areas) {
          const { storageLocations, ...areaFields } = area;
          const createdArea = await single(
            orpcClient.warehouseArea.create([
              {
                ...areaFields,
                warehouseId: result.id,
              },
            ])
          );

          if (createdArea && storageLocations?.length) {
            await allOrThrow(
              orpcClient.storageLocation.create(
                storageLocations.map((location) => ({
                  ...location,
                  warehouseAreaId: createdArea.id,
                }))
              )
            );
          }
        }

        api.info({
          message: `Notification `,
          description: <div>{t("warehouse.warehouseCreatedSuccess")}</div>,
          placement: "topLeft",
          duration: 0,
        });
        navigate("/hrMainPage/warehouse");
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      api.error({
        message: `Notification `,
        description: <div>{String(error)}</div>,
        placement: "topLeft",
        duration: 0,
      });
    }
  };

  const handleDataChange = (name, value) => {
    setWarehouseData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleTempAreaChange = (name, value) => {
    setTempArea((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleTempLocChange = (name, value) => {
    setTempLoc((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeAddressData = (value, type) => {
    setWarehouseData((prevData) => ({
      ...prevData,
      address: {
        ...prevData?.address,
        [type]:
          type === "postalCode" ? (value === "" ? "" : Number(value)) : value,
      },
    }));
  };

  const handleDisplayEnable = (value) => {
    return value === t("warehouse.enabledValue");
  };

  const handleDisplayEnableValue = (enable) => {
    return enable ? t("warehouse.enabledValue") : t("warehouse.disabledValue");
  };

  const handleDisplaySelectMembers = (data) => {
    return data?.map((t) => t?.name + ` (ID: ${t?.id})`);
  };

  const handleContactData = (e) => {
    if (!e) return;

    const match = e?.match(/ID:\s*([^\)]+)/);
    const idNumber = match ? match[1] : null;

    const selectedData = employeeData.find((emp) => emp?.id === idNumber);

    return selectedData?.id;
  };

  const handleAddArea = () => {
    setAreaModalMode("new");
    setTempArea(defaultTempArea);
    setAreaModalOpen(true);
  };

  const handleEditArea = (area, index) => {
    setAreaModalMode("edit");
    setTempArea(area);
    setEditAreaIndex(index);
    setAreaModalOpen(true);
  };

  const handleDeleteArea = (index) => {
    setAreas((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveArea = () => {
    if (areaModalMode === "new") {
      setAreas((prev) => [...prev, { ...tempArea, storageLocations: [] }]);
    } else {
      setAreas((prev) => {
        const updated = [...prev];
        updated[editAreaIndex] = {
          ...updated[editAreaIndex],
          ...tempArea,
        };
        return updated;
      });
    }
    setTempArea(defaultTempArea);
    setAreaModalOpen(false);
  };

  const handleAddLoc = (areaIndex) => {
    setLocModalMode("new");
    setTempLoc(defaultTempStorageLocation);
    setLocAreaIndex(areaIndex);
    setLocModalOpen(true);
  };

  const handleEditLoc = (areaIndex, location, locIndex) => {
    setLocModalMode("edit");
    setTempLoc(location);
    setLocAreaIndex(areaIndex);
    setEditLocIndex(locIndex);
    setLocModalOpen(true);
  };

  const handleDeleteLoc = (areaIndex, locIndex) => {
    setAreas((prev) =>
      prev.map((area, i) =>
        i !== areaIndex
          ? area
          : {
              ...area,
              storageLocations: area.storageLocations.filter(
                (_, j) => j !== locIndex
              ),
            }
      )
    );
  };

  const handleSaveLoc = () => {
    setAreas((prev) =>
      prev.map((area, i) => {
        if (i !== locAreaIndex) return area;
        const storageLocations = [...area.storageLocations];
        if (locModalMode === "new") {
          storageLocations.push(tempLoc);
        } else {
          storageLocations[editLocIndex] = tempLoc;
        }
        return { ...area, storageLocations };
      })
    );
    setTempLoc(defaultTempStorageLocation);
    setLocModalOpen(false);
  };

  const {
    name,
    enable,
    description,
    address: { city, district, postalCode, address: street },
  } = warehouseData;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await orpcClient.employee.list({});
        setEmployeeData(result || []);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  const renderControl = (label, control, Icon) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      {control}
    </div>
  );

  return (
    <div className="min-h-screen p-8">
      {contextHolder}
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("warehouse.createWarehouse")}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("warehouse.add") || "新增倉庫"}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconBuildingWarehouse size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.basicInfo")}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderControl(
              t("warehouse.warehouseName") || "倉庫名稱",
              <Input
                value={name}
                onChange={({ target: { value } }) =>
                  handleDataChange("name", value)
                }
                leftSection={<IconBuildingWarehouse size={16} />}
              />,
              IconBuildingWarehouse
            )}
            {renderControl(
              t("warehouse.enable"),
              <Select
                value={handleDisplayEnableValue(enable)}
                data={[
                  t("warehouse.enabledValue"),
                  t("warehouse.disabledValue"),
                ]}
                onChange={(value) =>
                  handleDataChange("enable", handleDisplayEnable(value))
                }
                leftSection={<IconToggleLeft size={16} />}
              />,
              IconCircleCheck
            )}
            {renderControl(
              t("warehouse.warehouseDescriptionLabel"),
              <Input
                value={description}
                onChange={({ target: { value } }) =>
                  handleDataChange("description", value)
                }
                leftSection={<IconFileText size={16} />}
              />,
              IconFileText
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconMapPin size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.warehouseAddress") || "倉庫地址"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
            {renderControl(
              t("warehouse.city"),
              <Input
                leftSection={<IconMapPin size={16} />}
                value={city}
                onChange={(e) => handleChangeAddressData(e.target.value, "city")}
              />,
              IconMapPin
            )}
            {renderControl(
              t("warehouse.district"),
              <Input
                leftSection={<IconMapPin size={16} />}
                value={district}
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "district")
                }
              />,
              IconMapPin
            )}
            {renderControl(
              t("warehouse.postalCode"),
              <Input
                leftSection={<IconHash size={16} />}
                value={postalCode?.toString() || ""}
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "postalCode")
                }
              />,
              IconHash
            )}
            {renderControl(
              t("warehouse.address") || "地址",
              <Input
                leftSection={<IconHome size={16} />}
                value={street}
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "address")
                }
              />,
              IconHome
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconUser size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.contactPerson")}
            </h3>
          </div>
          <div className="p-6">
            {renderControl(
              t("warehouse.contactPerson"),
              <Autocomplete
                placeholder={
                  employeeData && employeeData.length > 0
                    ? t("warehouse.selectContact")
                    : t("warehouse.noEmployeeData")
                }
                leftSection={<IconUser size={16} />}
                data={handleDisplaySelectMembers(employeeData)}
                onChange={(e) =>
                  handleDataChange("contactId", handleContactData(e))
                }
                onOptionSubmit={(e) =>
                  handleDataChange("contactId", handleContactData(e))
                }
                disabled={!employeeData || employeeData.length === 0}
              />,
              IconUser
            )}
          </div>
        </div>

        {/* 儲位管理 section commented out
        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center gap-2">
              <IconStack2 size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">
                {t("warehouse.areaSection")}
              </h3>
            </div>
            <Button
              variant="default"
              radius="md"
              size="sm"
              onClick={handleAddArea}
            >
              {t("warehouse.addArea")}
            </Button>
          </div>
          <div className="space-y-4 p-6">
            {areas && areas.length ? (
              areas.map((area, areaIndex) => (
                <div
                  key={areaIndex}
                  className="overflow-hidden rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/60 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <IconStack2 size={15} className="text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        {area.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {area.enable
                          ? t("warehouse.enabledValue")
                          : t("warehouse.disabledValue")}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="xs"
                        radius="md"
                        onClick={() => handleAddLoc(areaIndex)}
                      >
                        {t("warehouse.addStorage")}
                      </Button>
                      <Button
                        variant="default"
                        size="xs"
                        radius="md"
                        onClick={() => handleEditArea(area, areaIndex)}
                      >
                        {t("warehouse.editBtn")}
                      </Button>
                      <Button
                        size="xs"
                        radius="md"
                        color="red"
                        onClick={() => handleDeleteArea(areaIndex)}
                      >
                        {t("warehouse.deleteBtn")}
                      </Button>
                    </div>
                  </div>
                  <div className="p-4">
                    {area.storageLocations && area.storageLocations.length ? (
                      <Table highlightOnHover>
                        <Table.Thead>
                          <Table.Tr style={{ color: "#6F6F6F" }}>
                            <Table.Th>
                              {t("warehouse.storageLocationName") || "儲位名稱"}
                            </Table.Th>
                            <Table.Th>{t("warehouse.enable")}</Table.Th>
                            <Table.Th>{t("warehouse.description")}</Table.Th>
                            <Table.Th>{t("warehouse.actions") || "操作"}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {area.storageLocations.map((location, locIndex) => (
                            <Table.Tr key={locIndex}>
                              <Table.Td>{location.name}</Table.Td>
                              <Table.Td>
                                {location.enable
                                  ? t("warehouse.enabledValue")
                                  : t("warehouse.disabledValue")}
                              </Table.Td>
                              <Table.Td>{location.description}</Table.Td>
                              <Table.Td>
                                <div className="flex gap-2">
                                  <Button
                                    variant="default"
                                    size="xs"
                                    radius="md"
                                    onClick={() =>
                                      handleEditLoc(areaIndex, location, locIndex)
                                    }
                                  >
                                    {t("warehouse.editBtn")}
                                  </Button>
                                  <Button
                                    size="xs"
                                    radius="md"
                                    color="red"
                                    onClick={() =>
                                      handleDeleteLoc(areaIndex, locIndex)
                                    }
                                  >
                                    {t("warehouse.deleteBtn")}
                                  </Button>
                                </div>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    ) : (
                      <div className="text-center text-xs text-gray-400">
                        {t("warehouse.storageLocationSection")}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-sm text-gray-400">
                {t("warehouse.noAreaYet")}
              </div>
            )}
          </div>
        </div>
        */}

        <div className="flex gap-4">
          <Button
            variant="default"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => navigate(`/hrMainPage/warehouse`)}
          >
            {t("warehouse.cancel")}
          </Button>
          <Button
            variant="filled"
            color="gray"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => handleSubmit(warehouseData, areas)}
          >
            {t("warehouse.create")}
          </Button>
        </div>
      </div>

      {/* 儲位管理 modals commented out
      <Modal
        open={areaModalOpen}
        onOk={handleSaveArea}
        onCancel={() => {
          setTempArea(defaultTempArea);
          setAreaModalOpen(false);
        }}
      >
        <div className="space-y-4">
          <div className="text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {tempArea?.name || t("warehouse.areaName")}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderControl(
              t("warehouse.areaName"),
              <Input
                value={tempArea?.name}
                onChange={({ target: { value } }) =>
                  handleTempAreaChange("name", value)
                }
                leftSection={<IconStack2 size={16} />}
              />,
              IconStack2
            )}
            {renderControl(
              t("warehouse.enable"),
              <AntdSelect
                options={[
                  {
                    value: t("warehouse.enabledValue"),
                    label: t("warehouse.enabledValue"),
                  },
                  {
                    value: t("warehouse.disabledValue"),
                    label: t("warehouse.disabledValue"),
                  },
                ]}
                value={
                  tempArea?.enable
                    ? t("warehouse.enabledValue")
                    : t("warehouse.disabledValue")
                }
                onChange={(value) =>
                  handleTempAreaChange("enable", handleDisplayEnable(value))
                }
                style={{ width: "100%" }}
              />,
              IconCircleCheck
            )}
          </div>
          {renderControl(
            t("warehouse.areaDescription"),
            <Input
              value={tempArea?.description}
              onChange={({ target: { value } }) =>
                handleTempAreaChange("description", value)
              }
              leftSection={<IconFileText size={16} />}
            />,
            IconFileText
          )}
          {renderControl(
            t("warehouse.picture"),
            <Input
              value={tempArea?.picture}
              placeholder={t("warehouse.pictureUrlPlaceholder")}
              onChange={({ target: { value } }) =>
                handleTempAreaChange("picture", value)
              }
              leftSection={<IconPhoto size={16} />}
            />,
            IconPhoto
          )}
        </div>
      </Modal>

      <Modal
        open={locModalOpen}
        onOk={handleSaveLoc}
        onCancel={() => {
          setTempLoc(defaultTempStorageLocation);
          setLocModalOpen(false);
        }}
      >
        <div className="space-y-4">
          <div className="text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {tempLoc?.name || t("warehouse.storageLocationName")}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderControl(
              t("warehouse.storageLocationName") || "儲位名稱",
              <Input
                value={tempLoc?.name}
                onChange={({ target: { value } }) =>
                  handleTempLocChange("name", value)
                }
                leftSection={<IconBuildingWarehouse size={16} />}
              />,
              IconBuildingWarehouse
            )}
            {renderControl(
              t("warehouse.enable"),
              <AntdSelect
                options={[
                  {
                    value: t("warehouse.enabledValue"),
                    label: t("warehouse.enabledValue"),
                  },
                  {
                    value: t("warehouse.disabledValue"),
                    label: t("warehouse.disabledValue"),
                  },
                ]}
                value={
                  tempLoc?.enable
                    ? t("warehouse.enabledValue")
                    : t("warehouse.disabledValue")
                }
                onChange={(value) =>
                  handleTempLocChange("enable", handleDisplayEnable(value))
                }
                style={{ width: "100%" }}
              />,
              IconCircleCheck
            )}
          </div>
          {renderControl(
            t("warehouse.fieldDescription"),
            <Input
              value={tempLoc?.description}
              onChange={({ target: { value } }) =>
                handleTempLocChange("description", value)
              }
              leftSection={<IconFileText size={16} />}
            />,
            IconFileText
          )}
          {renderControl(
            t("warehouse.picture"),
            <Input
              value={tempLoc?.picture}
              placeholder={t("warehouse.pictureUrlPlaceholder")}
              onChange={({ target: { value } }) =>
                handleTempLocChange("picture", value)
              }
              leftSection={<IconPhoto size={16} />}
            />,
            IconPhoto
          )}
        </div>
      </Modal>
      */}
    </div>
  );
}
export default AddWarehouseListPage;
